import { useEffect, useState, useCallback } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, buildFileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ListMusic, Plus, Trash2, Pencil, Tv, Play, ImagePlus, Clock } from "lucide-react";
import { toast } from "sonner";
import PlaylistPreviewDialog from "@/components/PlaylistPreviewDialog";
import NYClock from "@/components/NYClock";

export default function PlaylistsPage() {
  const { activeBusiness } = useBusiness();
  const [playlists, setPlaylists] = useState([]);
  const [tvs, setTvs] = useState([]);
  const [media, setMedia] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    const [pl, tv, md] = await Promise.all([
      apiAdmin.get(`/playlists?business_id=${activeBusiness.id}`),
      apiAdmin.get(`/tvs?business_id=${activeBusiness.id}`),
      apiAdmin.get(`/media?business_id=${activeBusiness.id}`),
    ]);
    setPlaylists(pl.data);
    setTvs(tv.data);
    setMedia(md.data);
  }, [activeBusiness]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await apiAdmin.delete(`/playlists/${id}`);
      toast.success("Playlist deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="playlists-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Programming</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Playlists
          </h1>
          <p className="text-sm text-slate-500 mt-1">Group media into reusable loops, then assign them to screens.</p>
        </div>
        <div className="flex items-center gap-3">
          <NYClock />
          <Button onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="playlist-create-open-button">
            <Plus className="h-4 w-4 mr-2" /> New playlist
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {playlists.map((pl) => {
          const tv = tvs.find((t) => t.id === pl.tv_id);
          return (
            <div
              key={pl.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md hover:-translate-y-[2px] transition-all duration-300"
              data-testid={`playlist-card-${pl.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 grid place-items-center">
                    <ListMusic className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-semibold text-slate-900">{pl.name}</div>
                      {pl.always_on && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                          data-testid={`playlist-${pl.id}-always-on-badge`}
                        >
                          <Play className="h-2.5 w-2.5" strokeWidth={2.5} /> 24/7
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{pl.media_ids.length} items · {pl.item_duration}s each</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <Tv className="h-3.5 w-3.5" />
                {tv ? <span>Assigned to <b className="text-slate-700">{tv.name}</b></span> : <span>Unassigned</span>}
              </div>

              <div className="mt-3 grid grid-cols-5 gap-1.5">
                {pl.media_ids.slice(0, 5).map((mid) => {
                  const m = media.find((x) => x.id === mid);
                  if (!m) return <div key={mid} className="aspect-square rounded bg-slate-100" />;
                  return m.media_type === "image" ? (
                    <img key={mid} src={buildFileUrl(m.id, "admin")} alt="" className="aspect-square rounded object-cover" />
                  ) : (
                    <div key={mid} className="aspect-square rounded bg-slate-900 text-white text-[9px] uppercase tracking-widest grid place-items-center">
                      Video
                    </div>
                  );
                })}
                {pl.media_ids.length === 0 && (
                  <div className="col-span-5 text-xs text-slate-400 italic py-2">No media yet</div>
                )}
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setPreview(pl)}
                  disabled={pl.media_ids.length === 0}
                  data-testid={`playlist-${pl.id}-play`}
                >
                  <Play className="h-4 w-4 mr-1.5" /> Play
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(pl)}
                  data-testid={`playlist-${pl.id}-add-media`}
                >
                  <ImagePlus className="h-4 w-4 mr-1.5" strokeWidth={1.25} /> Add media
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(pl)} data-testid={`playlist-${pl.id}-edit`}>
                  <Pencil className="h-4 w-4 mr-1.5" /> Edit
                </Button>
                <Button
                  variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                  onClick={() => remove(pl.id)}
                  data-testid={`playlist-${pl.id}-delete`}
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              </div>
            </div>
          );
        })}

        {playlists.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <div className="font-heading text-xl font-semibold text-slate-900">No playlists yet</div>
            <p className="text-sm text-slate-500 mt-1">Create your first playlist to start programming screens.</p>
          </div>
        )}
      </div>

      <PlaylistEditor
        open={creating || !!editing}
        playlist={editing}
        tvs={tvs}
        media={media}
        business={activeBusiness}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={load}
      />
    </div>
  );
}

function PlaylistEditor({ open, playlist, tvs, media, business, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [tvIds, setTvIds] = useState([]);     // multi-TV assignment
  const [duration, setDuration] = useState(8);
  const [selected, setSelected] = useState([]);
  const [alwaysOn, setAlwaysOn] = useState(false);

  // ---- Schedule (mandatory unless alwaysOn) ----
  const [startH, setStartH] = useState(9);  const [startM, setStartM] = useState(0);  const [startP, setStartP] = useState("AM");
  const [endH, setEndH] = useState(12);    const [endM, setEndM] = useState(0);     const [endP, setEndP] = useState("PM");
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [scheduleLabel, setScheduleLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(playlist?.name || "");
      setTvIds(playlist?.tv_id ? [playlist.tv_id] : []);
      setDuration(playlist?.item_duration || 8);
      setSelected(playlist?.media_ids || []);
      setAlwaysOn(playlist?.always_on || false);
      setStartH(9); setStartM(0); setStartP("AM");
      setEndH(12); setEndM(0); setEndP("PM");
      setDays([0, 1, 2, 3, 4]);
      setScheduleLabel("");
    }
  }, [open, playlist]);

  if (!business) return null;

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleTv = (id) =>
    setTvIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleDay = (d) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));

  const to24 = (h, p) => (p === "AM" ? (h % 12) : (h % 12) + 12);
  const startMinutes = to24(startH, startP) * 60 + startM;
  const endMinutes = to24(endH, endP) * 60 + endM;

  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (!tvIds.length) return toast.error("Pick at least one TV — playlists must play somewhere");
    if (!alwaysOn) {
      if (!days.length) return toast.error("Pick at least one day for the schedule");
      if (startMinutes === endMinutes) return toast.error("Schedule start and end cannot be equal");
    }
    setSaving(true);
    try {
      // 1. Create / update the playlist
      const body = {
        name,
        tv_id: tvIds[0] || null,
        item_duration: Number(duration) || 8,
        media_ids: selected,
        always_on: alwaysOn,
      };
      let playlistId;
      if (playlist) {
        await apiAdmin.patch(`/playlists/${playlist.id}`, body);
        playlistId = playlist.id;
      } else {
        const { data } = await apiAdmin.post("/playlists", { ...body, business_id: business.id });
        playlistId = data.id;
      }

      // 2. (No default assignment) — playlists run strictly inside their schedule windows.

      // 3. Schedule (skipped when alwaysOn — the TV will play this playlist 24/7
      //    via the always_on fallback in the backend resolver).
      if (!alwaysOn) {
        await Promise.all(
          tvIds.map((id) =>
            apiAdmin.post(`/tvs/${id}/schedules`, {
              playlist_id: playlistId,
              start_minute: startMinutes,
              end_minute: endMinutes,
              days_of_week: days,
              enabled: true,
              label: scheduleLabel.trim() || name,
            }).catch(() => {})
          )
        );
      }

      toast.success(
        playlist
          ? "Playlist updated"
          : alwaysOn
            ? `Playlist created — playing 24/7 on ${tvIds.length} TV${tvIds.length > 1 ? "s" : ""}`
            : `Playlist created and scheduled on ${tvIds.length} TV${tvIds.length > 1 ? "s" : ""}`
      );
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="playlist-editor-dialog">
        <DialogHeader>
          <DialogTitle>{playlist ? "Edit playlist" : "New playlist"}</DialogTitle>
          <DialogDescription>
            Pick media, choose which TVs play it, and set the schedule window.
          </DialogDescription>
        </DialogHeader>

        {/* Basic fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="playlist-editor-name-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Seconds per image</Label>
            <Input
              type="number" value={duration} onChange={(e) => setDuration(e.target.value)} min={3} max={120}
              className="mt-1.5" data-testid="playlist-editor-duration-input"
            />
          </div>
        </div>

        {/* TV assignment — multi select */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Play on these TVs</Label>
            <span className="text-xs text-slate-500" data-testid="playlist-editor-tv-count">
              {tvIds.length} of {tvs.length} selected
            </span>
          </div>
          {tvs.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6 rounded-xl bg-slate-50 border border-dashed border-slate-200">
              No TVs yet. Add one from the Televisions page.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {tvs.map((t) => {
                const active = tvIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTv(t.id)}
                    className={`text-left rounded-xl border px-3.5 py-2.5 flex items-center gap-2.5 transition-all ${
                      active
                        ? "border-blue-500 bg-blue-50 ring-1 ring-blue-100"
                        : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300"
                    }`}
                    data-testid={`playlist-editor-tv-${t.id}`}
                  >
                    <div
                      className={`h-4 w-4 rounded-md border-2 grid place-items-center shrink-0 ${
                        active ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"
                      }`}
                    >
                      {active && <span className="text-white text-[10px] leading-none">✓</span>}
                    </div>
                    <Tv className={`h-3.5 w-3.5 shrink-0 ${active ? "text-blue-600" : "text-slate-400"}`} strokeWidth={1.75} />
                    <div className="min-w-0">
                      <div className={`text-sm font-medium truncate ${active ? "text-blue-900" : "text-slate-800"}`}>{t.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{t.location}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Always-on (24/7) toggle — overrides schedule */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setAlwaysOn((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3"
            data-testid="playlist-editor-always-on-toggle"
          >
            <div className="flex items-center gap-2.5">
              <Play className={`h-4 w-4 ${alwaysOn ? "text-emerald-600" : "text-slate-400"}`} strokeWidth={1.75} />
              <div className="text-left">
                <div className="text-sm font-medium text-slate-900">Play 24/7 (no schedule)</div>
                <div className="text-[11px] text-slate-500">
                  {alwaysOn
                    ? "Loops continuously on the selected TVs. Scheduled playlists (if any) will override during their window."
                    : "Off — the playlist only plays inside the schedule below."}
                </div>
              </div>
            </div>
            <div
              className={`h-6 w-11 rounded-full p-0.5 transition-colors ${
                alwaysOn ? "bg-emerald-600" : "bg-slate-300"
              }`}
            >
              <div
                className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  alwaysOn ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </div>
          </button>
        </div>

        {/* Schedule (mandatory unless 24/7 toggle is on) */}
        {!alwaysOn && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white">
          <div className="w-full flex items-center gap-2.5 px-4 py-3 border-b border-slate-100">
            <Clock className="h-4 w-4 text-amber-600" strokeWidth={1.75} />
            <div className="text-left">
              <div className="text-sm font-medium text-slate-900">When this playlist plays (New York time)</div>
              <div className="text-[11px] text-slate-500">
                Active only during the hours and days selected below. Outside the window the TV is idle.
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3" data-testid="playlist-editor-schedule-fields">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Start time</Label>
                <TimePicker12hMini
                  hour={startH} minute={startM} period={startP}
                  onHour={setStartH} onMinute={setStartM} onPeriod={setStartP}
                  testidPrefix="playlist-schedule-start"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">End time</Label>
                <TimePicker12hMini
                  hour={endH} minute={endM} period={endP}
                  onHour={setEndH} onMinute={setEndM} onPeriod={setEndP}
                  testidPrefix="playlist-schedule-end"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Days</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {DAY_LABELS.map((d, i) => {
                  const active = days.includes(i);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(i)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "border-amber-500 bg-amber-500 text-white"
                          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`playlist-schedule-day-${i}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Label (optional)</Label>
              <Input
                value={scheduleLabel} onChange={(e) => setScheduleLabel(e.target.value)}
                placeholder="e.g. Happy Hour, Brunch"
                className="mt-1.5"
                data-testid="playlist-editor-schedule-label-input"
              />
            </div>
            {tvIds.length === 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>Schedules need at least one TV. Select TVs above for this to take effect.</span>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Media items — vertical list with thumbnail + filename for easy scanning */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Media items</Label>
            <span className="text-xs text-slate-500">{selected.length} selected</span>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white max-h-80 overflow-y-auto divide-y divide-slate-100">
            {media.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-8">
                No media uploaded yet.
              </div>
            )}
            {media.map((m) => {
              const isActive = selected.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                    isActive ? "bg-blue-50" : "hover:bg-slate-50"
                  }`}
                  data-testid={`playlist-editor-media-${m.id}`}
                >
                  <div
                    className={`h-5 w-5 rounded-md border-2 grid place-items-center shrink-0 ${
                      isActive ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"
                    }`}
                  >
                    {isActive && <span className="text-white text-[11px] leading-none font-bold">✓</span>}
                  </div>
                  <div className="h-10 w-14 rounded-lg overflow-hidden bg-slate-100 shrink-0 relative">
                    {m.media_type === "image" ? (
                      <img
                        src={buildFileUrl(m.id, "admin")}
                        alt={m.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center bg-slate-900 text-white text-[9px] uppercase tracking-widest font-semibold">
                        Video
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm truncate ${isActive ? "text-blue-900 font-medium" : "text-slate-900"}`}>
                      {m.filename}
                    </div>
                    <div className="text-[11px] text-slate-500 tabular-nums">
                      {m.media_type === "video" ? "Video" : "Image"}
                      {m.size ? ` · ${m.size >= 1024 * 1024 ? (m.size / 1024 / 1024).toFixed(1) + " MB" : (m.size / 1024).toFixed(0) + " KB"}` : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="playlist-editor-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300" data-testid="playlist-editor-submit">
            {saving ? "Saving…" : playlist ? "Save changes" : "Create playlist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Inline 12h time picker (smaller variant for the playlist editor) */
function TimePicker12hMini({ hour, minute, period, onHour, onMinute, onPeriod, testidPrefix }) {
  return (
    <div className="mt-1.5 flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white p-1 focus-within:border-blue-500 transition-colors">
      <Select value={String(hour)} onValueChange={(v) => onHour(Number(v))}>
        <SelectTrigger className="h-8 border-0 shadow-none focus:ring-0 px-2 font-mono text-sm w-[60px]" data-testid={`${testidPrefix}-hour`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <SelectItem key={h} value={String(h)}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-slate-400 font-mono select-none">:</span>
      <Select value={String(minute).padStart(2, "0")} onValueChange={(v) => onMinute(Number(v))}>
        <SelectTrigger className="h-8 border-0 shadow-none focus:ring-0 px-2 font-mono text-sm w-[60px]" data-testid={`${testidPrefix}-minute`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
            <SelectItem key={m} value={String(m).padStart(2, "0")}>{String(m).padStart(2, "0")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex rounded-md bg-slate-100 p-0.5 ml-auto">
        {["AM", "PM"].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPeriod(p)}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${
              period === p ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`${testidPrefix}-period-${p.toLowerCase()}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
